import React from "react";
import "./DoughnutChart.scss";
import { ResponsivePie } from "@nivo/pie";
import colors from "assets/styles/mixins/colors.scss";
import {
  buildChartData,
  convertToNivoData,
  formatNumber,
  getChartTitle,
  getChartTitleNum,
  getColorMapping,
  renderLegends,
} from "utils/dashboardDoughnutHelper";
import { FormattedMessage } from "react-intl";
import LoaderV2 from "core/loader/LoaderV2";
import { QUERY_STATUS } from "constants/constants";
import { useDoughnutNavigation } from "hooks/useDoughnutNavigation";
import PropTypes from "prop-types";

const ChartTooltip = ({ datum }) => (
  <div className="doughnut-chart-tooltip">
    <div
      style={{
        width: "15px",
        height: "15px",
        borderRadius: "50%",
        backgroundColor: datum.color,
        flexShrink: 0,
      }}
    />
    <span>
      {formatNumber(datum.data.realValue)} {datum.label}
    </span>
  </div>
);

ChartTooltip.propTypes = {
  datum: PropTypes.shape({
    color: PropTypes.string,
    label: PropTypes.string,
    data: PropTypes.shape({
      realValue: PropTypes.number,
    }),
  }).isRequired,
};

const ChartOverlay = ({ titleNum, titleCount, titleLegend, url }) => (
  <div className="overlay">
    <div className="overlay-content">
      <div className="overlay-content__number">{titleNum}</div>
      <div
        className="overlay-content__title"
        onClick={() => {
          window.location.href = url;
        }}
      >
        <FormattedMessage
          id={titleLegend}
          defaultMessage="chart title"
          values={{ count: titleCount }}
        />
      </div>
    </div>
  </div>
);

ChartOverlay.propTypes = {
  titleNum: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  titleCount: PropTypes.number,
  titleLegend: PropTypes.string,
  url: PropTypes.string,
};

const DoughnutChart = ({ stats, loadingStatus, lang, userScope }) => {
  const handleSliceClick = useDoughnutNavigation(userScope);

  const getNivoConfig = (data) => {
    const colorMapping = getColorMapping(data);

    return {
      innerRadius: 0.85,
      padAngle: 0.5,
      cornerRadius: 0,
      margin: { top: 20, right: 50, bottom: 20, left: 50 },
      colors: ({ id }) => colorMapping[id],
      borderWidth: 0,
      enableArcLabels: false,
      enableArcLinkLabels: false,
      animate: true,
      motionConfig: "gentle",
      isInteractive: true,
      onClick: handleSliceClick,
      tooltip: ChartTooltip,
      activeOuterRadiusOffset: 7,
    };
  };

  const formatDataForDoughnutChart = (data) =>
    Object.entries(data)?.map(([chartType, [urlData, statsData]]) => {
      if (!chartType) return null;

      return {
        titleLegend: getChartTitle(chartType),
        titleNum: getChartTitleNum(chartType, statsData.statistics),
        titleCount: Number(statsData.statistics?.total) || 0,
        dataPercentage: statsData.statistics,
        data: buildChartData(chartType, statsData.statistics, lang),
        url: urlData.url,
      };
    });

  const hasDataLoaded = Object.values(loadingStatus).some(
    (status) => status === QUERY_STATUS.SUCCESS
  );

  const isChartDataReady = (chart) => {
    return chart?.titleNum !== undefined && chart?.data?.length > 1;
  };

  const EmptyChart = () => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        color: colors.grayChart,
      }}
    >
      No data available
    </div>
  );

  const renderChart = (chart) => {
    const chartReady = isChartDataReady(chart);
    const nivoData = convertToNivoData(chart?.data);

    return (
      <div key={chart?.titleLegend} className="doughnut-chart-item">
        {(!hasDataLoaded || !chartReady) && (
          <div className="doughnut-loader" style={{ marginTop: "10rem" }}>
            <LoaderV2 key={chart?.titleLegend} />
          </div>
        )}
        {hasDataLoaded && chartReady && (
          <div className="doughnut-wrapper" key={chart?.titleLegend}>
            <div className="doughnut-sub-wrapper">
              <ChartOverlay
                titleNum={chart.titleNum}
                titleCount={chart.titleCount}
                titleLegend={chart.titleLegend}
                url={chart.url}
              />

              <div
                style={{
                  width: "250px",
                  height: "450px",
                  overflow: "visible",
                }}
              >
                {nivoData.length > 0 ? (
                  <ResponsivePie {...getNivoConfig(nivoData)} data={nivoData} />
                ) : (
                  <EmptyChart />
                )}
              </div>
            </div>
            {renderLegends(chart)}
          </div>
        )}
      </div>
    );
  };

  return formatDataForDoughnutChart(stats)?.map(renderChart);
};

DoughnutChart.propTypes = {
  stats: PropTypes.object,
  loadingStatus: PropTypes.object,
  lang: PropTypes.string,
  userScope: PropTypes.string,
};

export default DoughnutChart;
